import React from 'react';
import * as FiIcons from 'react-icons/fi';
import {useNavigate} from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import Button from '../ui/Button';
import Card from '../ui/Card';
import {
  getNextPlanName,
  getNextPlanType,
  getPlanLimit,
  getPlanName,
  normalizePlanType
} from '../../lib/billing/planLimits';
import {isIosConsumptionOnly} from '../../lib/billing/billingChannel';

const {FiArrowRight, FiCheck, FiX} = FiIcons;

function UpgradeModal({
  isOpen,
  onClose,
  planType = 'solo',
  businessCount = 0
}) {
  const navigate = useNavigate();

  if (!isOpen) {
    return null;
  }

  const currentPlan = normalizePlanType(planType);
  const nextPlan = getNextPlanType(currentPlan);
  const nextPlanName = getNextPlanName(currentPlan);
  const iosConsumptionOnly = isIosConsumptionOnly();

  const handleUpgrade = () => {
    onClose();
    navigate('/settings/billing?upgrade=true');
  };

  return (
    <div
      className="fixed inset-0 z-[80] overflow-y-auto bg-ink/40 px-4 py-8 backdrop-blur-sm"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="mx-auto flex min-h-full max-w-lg items-center">
        <Card
          interactive={false}
          className="w-full animate-fade-in-up overflow-hidden shadow-2xl"
          role="dialog"
          aria-modal="true"
          aria-labelledby="upgrade-modal-title"
        >
          <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-5 sm:px-8">
            <div>
              <p className="text-sm font-semibold text-primary">
                Business workspace
              </p>
              <h2
                id="upgrade-modal-title"
                className="mt-1 text-2xl font-bold tracking-tight text-ink"
              >
                {iosConsumptionOnly ? 'Business limit reached' : 'Unlock more Businesses'}
              </h2>
            </div>

            <button
              type="button"
              aria-label="Close upgrade dialog"
              onClick={onClose}
              className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-50 hover:text-ink"
            >
              <SafeIcon icon={FiX} className="h-5 w-5" />
            </button>
          </div>

          <div className="px-6 py-6 sm:px-8">
            <p className="text-sm leading-6 text-gray-600">
              {iosConsumptionOnly ? (
                'Additional Businesses are not available under your current plan in this iOS app.'
              ) : <>
              You&apos;ve reached the limit for your{' '}
              <strong className="text-ink">{getPlanName(currentPlan)}</strong>.
              {nextPlan ? (
                <>
                  {' '}
                  Upgrade to <strong className="text-ink">{nextPlanName}</strong>{' '}
                  to add more Businesses.
                </>
              ) : (
                ' Your current plan already includes the highest Business limit.'
              )}
              </>}
            </p>

            {!iosConsumptionOnly && <div className="mt-6 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5">
                <p className="text-xs font-bold uppercase tracking-wider text-gray-400">
                  Current plan
                </p>
                <p className="mt-2 font-bold text-ink">
                  {getPlanName(currentPlan)}
                </p>
                <p className="mt-3 text-2xl font-bold text-primary">
                  {getPlanLimit(currentPlan)}{' '}
                  <span className="text-sm font-medium text-gray-500">
                    Business{getPlanLimit(currentPlan) === 1 ? '' : 'es'}
                  </span>
                </p>
                <p className="mt-1 text-xs text-gray-500">
                  {businessCount} currently active
                </p>
              </div>

              <div className="rounded-2xl border border-primary/30 bg-mint p-5">
                <p className="text-xs font-bold uppercase tracking-wider text-primary">
                  Next plan
                </p>
                <p className="mt-2 font-bold text-ink">
                  {nextPlan ? nextPlanName : getPlanName(currentPlan)}
                </p>
                <p className="mt-3 text-2xl font-bold text-primary">
                  {nextPlan ? getPlanLimit(nextPlan) : getPlanLimit(currentPlan)}{' '}
                  <span className="text-sm font-medium text-primary">
                    Businesses
                  </span>
                </p>
                <p className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
                  <SafeIcon icon={FiCheck} className="h-3.5 w-3.5" />
                  More room to grow
                </p>
              </div>
            </div>}

            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button type="button" variant="ghost" onClick={onClose}>
                Not now
              </Button>

              {nextPlan && !iosConsumptionOnly && (
                <Button type="button" onClick={handleUpgrade}>
                  Upgrade to {nextPlanName}
                  <SafeIcon icon={FiArrowRight} className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default UpgradeModal;
